import { Capacitor } from "@capacitor/core";
import axios from "axios";
import { capacitorHttpAdapter } from "./adapters/capacitorHttpAdapter";
import { API_CONFIG } from "./constants/api";

// Only use CapacitorHttp adapter on native platforms (ios/android)
// BUT: Force use of web adapter in development (for ngrok/zrok/local testing)
// This avoids Capacitor HTTP issues with self-signed certs and tunnels
const isNativePlatform = Capacitor.isNativePlatform();
const isDevelopment =
	import.meta.env.DEV ||
	import.meta.env.MODE === "development" ||
	API_CONFIG.BASE_URL.includes("localhost") ||
	API_CONFIG.BASE_URL.includes("ngrok") ||
	API_CONFIG.BASE_URL.includes("zrok");
const useNativeHttp = isNativePlatform && !isDevelopment;

console.log("🌐 API Configuration:", {
	isNativePlatform,
	isDevelopment,
	useNativeHttp: useNativeHttp ? "Capacitor HTTP" : "Web HTTP (axios)",
	baseURL: API_CONFIG.BASE_URL,
});

export const api = axios.create({
	baseURL: API_CONFIG.BASE_URL,
	withCredentials: true,
	timeout: API_CONFIG.TIMEOUT,
	headers: {
		...API_CONFIG.DEFAULT_HEADERS,
		// Skip ngrok browser warning in development
		...(isDevelopment ? { "ngrok-skip-browser-warning": "true" } : {}),
	},
	...(useNativeHttp ? { adapter: capacitorHttpAdapter } : {}),
});

// Store auth callbacks
// Handler can be async to properly await logout completion
let onUnauthorized: (() => void | Promise<void>) | null = null;
let getAccessToken: (() => Promise<string>) | null = null;

// Store global loading handler
let setGlobalLoading: ((loading: boolean, message?: string) => void) | null =
	null;

export const setApiUnauthorizedHandler = (
	handler: () => void | Promise<void>,
) => {
	onUnauthorized = handler;
};

export const setApiTokenProvider = (tokenProvider: () => Promise<string>) => {
	getAccessToken = tokenProvider;
};

export const setApiLoadingHandler = (
	loadingHandler: (loading: boolean, message?: string) => void,
) => {
	setGlobalLoading = loadingHandler;
};

// Add request interceptor to attach token and show loading
api.interceptors.request.use(
	async (config) => {
		// Show global loading state
		if (setGlobalLoading) {
			setGlobalLoading(true);
		}

		// Check if this request should skip authentication
		// @ts-expect-error - skipAuth is a custom property we add to config
		const skipAuth = config.skipAuth === true;

		if (getAccessToken && !skipAuth) {
			try {
				const token = await getAccessToken();
				config.headers.Authorization = `Bearer ${token}`;
			} catch (error) {
				console.error("❌ [API] Failed to get access token:", error);

				// If token refresh fails, redirect to login to prevent infinite loops
				if (onUnauthorized) {
					onUnauthorized();
				}

				// Hide loading and reject the request
				if (setGlobalLoading) {
					setGlobalLoading(false);
				}
				return Promise.reject(
					new Error("Authentication failed - please log in again"),
				);
			}
		}
		return config;
	},
	(error) => {
		console.error("❌ [API] Request error:", error);
		// Hide loading on request error
		if (setGlobalLoading) {
			setGlobalLoading(false);
		}
		return Promise.reject(error);
	},
);

// Add response interceptor for 401 handling and hide loading
api.interceptors.response.use(
	(response) => {
		// Hide global loading state on successful response
		if (setGlobalLoading) {
			setGlobalLoading(false);
		}
		return response;
	},
	(error) => {
		// Hide global loading state on error response
		if (setGlobalLoading) {
			setGlobalLoading(false);
		}

		// Handle 401 Unauthorized errors
		if (error.response?.status === 401) {
			console.error("❌ [API] 401 Unauthorized - redirecting to login");

			// Call the unauthorized handler if set (redirect to login)
			if (onUnauthorized) {
				onUnauthorized();
			}
		} else {
			// Log other API errors with details
			console.error("❌ [API] Response error:", {
				message: error.message,
				code: error.code,
				status: error.response?.status,
				url: error.config?.url,
			});
		}

		return Promise.reject(error);
	},
);
